import { GETTEXT_ALL_FACILITIES } from '@/shared/lib';
import _ from 'lodash';

/**
 * Drops closed facilities from the user's support facility list. Mutates the given array in place, removing every entry whose status is 'Closed'.
 *
 * @param supportFacilities - Support facility list to prune in place.
 * @returns Nothing.
 */
const removeClosedFacilities = (supportFacilities: any) => {
  _.remove(supportFacilities, (x: any) => x.status === 'Closed');
};

/**
 * Decides which facility selection to persist once the user's support facilities have loaded. A single facility is mapped to a label/value option carrying its status, programs and flagged-study and ICOR permissions, while with several facilities the stored selection is kept when its id still exists and otherwise falls back to the All facilities option.
 *
 * @param supportFacilities - The user's support facilities.
 * @param storedSelection - Previously persisted facility selection.
 * @returns The facility selection to persist, or undefined when the stored one already matches.
 */
const resolveSelectedFacility = (supportFacilities: any, storedSelection: any) => {
  /**
   * Projects a support facility into the option shape persisted as the selected facility. Maps the name to label and the id to value and carries over the flagged-studies permission, status, programs and ICOR capability.
   *
   * @param facility - Support facility to project.
   * @returns The selected-facility option built from the facility.
   */
  const toSelection = (facility: any) => ({
    label: facility.name,
    value: facility.id,
    canViewFlaggedStudies: facility.canViewFlaggedStudies,
    status: facility.status,
    programs: facility?.programs,
    canUseIcor: facility?.canUseIcor,
  });

  if (supportFacilities?.length === 1) {
    // Compares the stored value against the facility *name* while the value it
    // writes is the facility *id*, so this is effectively always true. Preserved
    // as-is: it only causes a redundant rewrite, and changing it would alter
    // login behaviour.
    return storedSelection?.value !== supportFacilities[0].name
      ? toSelection(supportFacilities[0])
      : undefined;
  }

  const existedSelectedFacility = _.find(
    supportFacilities,
    (x: any) => x.id === storedSelection?.value,
  );

  return existedSelectedFacility
    ? toSelection(existedSelectedFacility)
    : { label: GETTEXT_ALL_FACILITIES, value: 'All facilities' };
};

export { removeClosedFacilities, resolveSelectedFacility };
